import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { WaterproofKit } from "../../../../../http/border/types/waterproofKits";
import { useBasesApi } from "../../../../../http/border/useBases";
import { useWaterproofKitsApi } from "../../../../../http/border/useWaterproofKits";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import MultiCheckbox from "../../../../ui/MultiCheckbox";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface WaterproofKitFormBody {
  item: SearchAdminItem | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  baseIds: string[];
}

interface WaterproofKitFormProps {
  waterproofKit?: WaterproofKit;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose: () => void;
}

const WaterproofKitForm: FC<WaterproofKitFormProps> = ({ waterproofKit, isDisabled, isLoading, onClose }) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [defaultItemOptions, setDefaultItemOptions] = useState<SearchAdminItem[]>([]);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());

  const itemsApi = useItemsApi();
  const basesApi = useBasesApi();
  const waterproofKitsApi = useWaterproofKitsApi();
  const bases = basesApi.useAdminBases({ limit: 100 }, aborter.current.signal);

  const defaultValues = {
    item: waterproofKit?.item,
    order: waterproofKit?.order ?? 50,
    visibility: waterproofKit?.visibility ?? "hidden",
    baseIds: waterproofKit?.bases.map((base) => base.item.id) ?? [],
  };

  return (
    <Form<WaterproofKitFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || isDisabled || isLoading}
      secondaryButton={
        <Button buttonProps={{ onClick: onClose }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        setIsSubmitting(true);

        if (waterproofKit?.item.id) {
          waterproofKitsApi
            .updateAdminWaterproofKit(waterproofKit.item.id, {
              order: formData.order!,
              visibility: formData.visibility,
              baseIds: formData.baseIds,
            })
            .then(() => onClose())
            .catch((err) => dispatch(showErrorNotification(i18n, err)))
            .finally(() => setIsSubmitting(false));
        } else {
          try {
            await waterproofKitsApi.insertAdminWaterproofKit({
              itemId: formData.item?.id ?? "",
              order: formData.order!,
              visibility: formData.visibility,
              baseIds: formData.baseIds,
            });
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
        }
      }}
    >
      {({ control, reset, setValue, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [waterproofKit]);

        const baseIds = watch("baseIds");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                    {({ labelId }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                      // Fetch initial data on mount
                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultItemOptions);
                      }, []);

                      return (
                        <CustomAsyncSelect
                          {...field}
                          inputId={labelId}
                          defaultOptions={defaultItemOptions}
                          isDisabled={!!waterproofKit || isDisabled}
                          loadOptions={loadOptions}
                          isClearable
                          getOptionLabel={itemLabel}
                          getOptionValue={(x) => x.id}
                          isInvalid={fieldState.invalid}
                          onChange={(value) => setValue("item", value ?? null)}
                        />
                      );
                    }}
                  </FormField>
                );
              }}
            />
            <FormField label={i18n.translation.bases.plural}>
              <MultiCheckbox
                disabled={isDisabled || isLoading || isSubmitting}
                values={
                  bases.data?.items.map((base) => ({
                    id: base.item.id,
                    title: itemLabel(base.item),
                    selected: baseIds.includes(base.item.id),
                  })) ?? []
                }
                onUpdate={(id, checked) => {
                  setValue("baseIds", checked ? [...baseIds, id] : baseIds.filter((baseId) => baseId !== id));
                }}
              />
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isSubmitting || isDisabled || isLoading}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isSubmitting || isDisabled || isLoading}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default WaterproofKitForm;
